import { apiRequest } from '../../../shared/services/apiClient';
import type {
  AssistantVoiceMode,
  AssistantVoiceSettings,
  UpdateAssistantVoiceSettingsRequest,
} from '../types/Company';

const pickSingle = (payload: any): any => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return payload;
};

const normalizeMode = (value: unknown): AssistantVoiceMode =>
  String(value ?? '').trim().toLowerCase() === 'pushtotalk' ? 'PushToTalk' : 'Duplex';

const numberOr = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const normalizeAssistantVoiceSettings = (value: any): AssistantVoiceSettings => ({
  enabled: value?.enabled !== false,
  mode: normalizeMode(value?.mode),
  voice: typeof value?.voice === 'string' && value.voice.trim() ? value.voice.trim() : 'alloy',
  speechModel: typeof value?.speechModel === 'string' && value.speechModel.trim()
    ? value.speechModel.trim()
    : 'gpt-4o-mini-tts',
  streamSpeech: value?.streamSpeech !== false,
  bargeInEnabled: value?.bargeInEnabled !== false,
  allowInternalAiDirectOnly: value?.allowInternalAiDirectOnly !== false,
  endpointingSilenceMs: numberOr(value?.endpointingSilenceMs, 650),
  minUtteranceMs: numberOr(value?.minUtteranceMs, 280),
  maxUtteranceSeconds: numberOr(value?.maxUtteranceSeconds, 20),
  vadRmsThreshold: numberOr(value?.vadRmsThreshold, 0.015),
  bargeInRmsThreshold: numberOr(value?.bargeInRmsThreshold, 0.045),
  bargeInHoldoffMs: numberOr(value?.bargeInHoldoffMs, 400),
  minTranscriptChars: numberOr(value?.minTranscriptChars, 2),
  speed: numberOr(value?.speed, 1),
  instructions: typeof value?.instructions === 'string' ? value.instructions : '',
  realtimeEnabled: value?.realtimeEnabled !== false,
  realtimeToolsEnabled: value?.realtimeToolsEnabled !== false,
  maxVoiceTokens: Math.max(0, Math.floor(numberOr(value?.maxVoiceTokens, 0))),
  voiceTokensUsed: Math.max(0, Math.floor(numberOr(value?.voiceTokensUsed, 0))),
  voiceTokensRemaining: value?.voiceTokensRemaining == null ? null : Math.max(0, Math.floor(numberOr(value.voiceTokensRemaining, 0))),
  isOverridden: Boolean(value?.isOverridden),
  availableVoices: Array.isArray(value?.availableVoices)
    ? value.availableVoices.filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0)
    : ['alloy'],
  availableModes: Array.isArray(value?.availableModes)
    ? value.availableModes.map(normalizeMode)
    : ['Duplex', 'PushToTalk'],
});

export const getAssistantVoiceSettings = async (): Promise<AssistantVoiceSettings> => {
  const payload = await apiRequest<any>('/api/v1/conversations/settings/voice');
  return normalizeAssistantVoiceSettings(pickSingle(payload));
};

export const updateAssistantVoiceSettings = async (
  request: UpdateAssistantVoiceSettingsRequest,
): Promise<AssistantVoiceSettings> => {
  const payload = await apiRequest<any>('/api/v1/conversations/settings/voice', {
    method: 'PUT',
    body: JSON.stringify(request),
  });
  return normalizeAssistantVoiceSettings(pickSingle(payload));
};

export const resetAssistantVoiceSettings = async (): Promise<AssistantVoiceSettings> => {
  const payload = await apiRequest<any>('/api/v1/conversations/settings/voice', {
    method: 'DELETE',
  });
  return normalizeAssistantVoiceSettings(pickSingle(payload));
};
